import React, { useState, useEffect } from 'react'
import { graphql, Link } from 'gatsby'
import pathOr from 'ramda/src/pathOr'
import pipe from 'ramda/src/pipe'
import sort from 'ramda/src/sort'
import cond from 'ramda/src/cond'
import equals from 'ramda/src/equals'
import keys from 'ramda/src/keys'
import groupBy from 'ramda/src/groupBy'
import { css } from 'styled-components'
import PropTypes from 'prop-types'
import { Box, Flex } from 'rebass'
import d from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { Paragraph } from '../components/Text'
import Layout from '../components/layout'
import SEO from '../components/seo'
import { ColorOverlay } from '../components/ui/ColorOverlay'
import Timeline from '../components/Timeline'
import SchemaCard from '../components/SchemaCard'

d.extend(isBetween)

const isLessThan45Min = event => d(event.end).diff(event.start, 'minutes') <= 45

const BREAK_HOUR = 7
const isBetweenMidnightAndBreakTime = (start, end, breakTime) => {
  const startHour = d(start).get('hour')
  const breakHour = d(breakTime).get('hour')
  return startHour >= 0 && startHour < breakHour
}

const getEvents = pathOr([], ['events', 'edges', 0, 'node', 'artistEvents'])
const hideSchema = pathOr(false, ['flags', 'edges', 0, 'node', 'hideSchema'])

const splitEveryByEven = list =>
  list.reduce(
    (acc, val, index) => {
      acc[index % 2 === 0 ? 0 : 1].push(val)
      return acc
    },
    [[], []]
  )

const byDay = groupBy(({ start, end }) => {
  const format = dt => dt.format('YYYY-MM-DD')
  const startDate = d(start)
  const endDate = d(end)
  const breakTime = d(endDate)
    .set('hour', BREAK_HOUR)
    .set('minute', 0)
  if (startDate.isSame(endDate, 'day')) {
    if (isBetweenMidnightAndBreakTime(startDate, endDate, breakTime)) {
      return format(startDate.date(startDate.date() - 1))
    }
    return format(startDate)
  }
  return endDate.isBefore(breakTime) ? format(startDate) : format(endDate)
})

const sortDays = sort((a, b) => (d(a).isBefore(d(b)) ? -1 : 1))

const sortByTime = sort(({ start: a }, { start: b }) => {
  const startA = d(a)
  const startB = d(b)
  if (startA.isSame(startB)) return 0
  if (startA.isAfter(startB)) return 1
  return -1
})

const eventsByDay = pipe(
  getEvents,
  sortByTime,
  byDay
)

const eventDays = pipe(
  eventsByDay,
  keys,
  sortDays
)

const dayLabel = cond([
  [equals('2019-08-25'), () => 'Sön'],
  [equals('2019-08-24'), () => 'Lör'],
  [equals('2019-08-23'), () => 'Fre'],
  [equals('2019-08-22'), () => 'Tor'],
])

const createLinkToArtist = ({ slug = '' }) => `/artist/${slug}`

const EventCard = ({ topOffset, end, start, title, image, venue, act = {}, ...rest }) => (
  <Link style={{ cursor: 'pointer' }} to={createLinkToArtist(act)}>
    <SchemaCard
      topOffset={topOffset}
      title={title}
      image={image}
      venue={venue}
      startDate={start}
      endDate={end}
      {...rest}
    />
  </Link>
)
EventCard.propTypes = {
  topOffset: PropTypes.number,
  end: PropTypes.string,
  start: PropTypes.string,
  title: PropTypes.string,
  image: PropTypes.object,
  venue: PropTypes.string,
  act: PropTypes.object,
}

const EventDays = ({ day, active, date, handleClick }) => (
  <Box
    onClick={() => handleClick(date)}
    m={1}
    my={2}
    p={1}
    css={{ cursor: 'pointer', borderBottom: active ? '3px solid white' : '' }}
  >
    <Paragraph fontFamily="regular" color="white" m={0} fontSize={[2, 3, 3]} fontWeight="normal">
      {day}
    </Paragraph>
  </Box>
)
EventDays.propTypes = {
  day: PropTypes.string,
  active: PropTypes.bool,
  date: PropTypes.string,
  handleClick: PropTypes.func,
}

const INTERVAL_MINUTES = 60
const INTERVAL_PX = 280

const calculateTopOffset = (startTime, indexTime) => {
  let diffInMinutes = d(startTime).diff(d(indexTime), 'minutes')
  if (diffInMinutes < 0) {
    diffInMinutes += 24 * 60
  }
  return (diffInMinutes || 0) * (INTERVAL_PX / 60)
}
const calculateEventCardHeight = (startTime, endTime) => {
  const diffInMinutes = d(endTime).diff(d(startTime), 'minutes')
  return (diffInMinutes || 0) * (INTERVAL_PX / 60)
}

const CardRow = ({ children, ...props }) => (
  <Flex width={1} alignItems="flex-start" mr={0} justifyContent="center" flexDirection="column" {...props}>
    {children}
  </Flex>
)
CardRow.propTypes = {
  children: PropTypes.node,
}

const SchemaPage = ({ data }) => {
  const hide = hideSchema(data)
  if (hide) {
    if (typeof window !== 'undefined') {
      window.location = '/'
    }
  }

  const [days] = useState(eventsByDay(data))
  const [activeDay, setActiveDay] = useState(eventDays(data)[0])
  const startTime = days[activeDay][0].start
  const endTime = days[activeDay][days[activeDay].length - 1].end
  const [e1, e2] = splitEveryByEven(days[activeDay])

  useEffect(() => {
    setActiveDay('2019-08-25')
  }, [])

  return (
    <Layout invertMenuLogo noPadding>
      <SEO title="PLX Tjärö 2019 - schema" keywords={['festival', 'tjärö', 'schema']} />
      <Box
        px="6px"
        css={css`
          z-index: 1;
          position: relative;
          margin-bottom: 100px;
        `}
      >
        <Flex
          flexDirection="row"
          justifyContent="center"
          mx="-6px"
          bg="red"
          css={css`
            border-bottom: 1px solid white;
            position: sticky;
            top: 0;
            z-index: 1;
          `}
        >
          {eventDays(data).map(date => (
            <EventDays
              key={date}
              active={date === activeDay}
              handleClick={setActiveDay}
              date={date}
              day={dayLabel(date)}
            />
          ))}
        </Flex>
        <Flex>
          <Flex width={1} css={{ position: 'relative', maxWidth: '374px', margin: '20px auto' }} flexDirection="row">
            <CardRow alignItems="flex-end">
              {e1.map(event => (
                <Box
                  key={`${event.id}${event.title}`}
                  css={{
                    maxWidth: 'calc(50% - 6px)',
                    width: '100%',
                    position: 'absolute',
                    top: `${calculateTopOffset(event.start, startTime)}px`,
                  }}
                >
                  <EventCard
                    {...event}
                    rescheduled={event.timeHasChanged}
                    small={isLessThan45Min(event)}
                    height={calculateEventCardHeight(event.start, event.end)}
                    title={event.act.title || event.title}
                    start={event.startTime}
                    end={event.endTime}
                    image={event.act.image.fluid}
                    venue={event.eventVenue.title}
                  />
                </Box>
              ))}
            </CardRow>
            <Flex css={{ position: 'relative' }} flexDirection="column">
              <Timeline
                formatter={date => d(date).format('HH:mm')}
                fromTime={new Date(startTime)}
                toTime={new Date(endTime)}
                interval={INTERVAL_MINUTES}
              >
                {(time, i) => (
                  <Box
                    key={`${time}.${i}`}
                    css={{
                      height: `${INTERVAL_PX}px`,
                      width: '11px',
                      position: 'relative',
                    }}
                  >
                    <Paragraph
                      css={{
                        transform: 'rotate(90deg)',
                      }}
                      fontSize="10px"
                      color="white"
                    >
                      {time.toString()}
                    </Paragraph>
                    <Box
                      key={time}
                      css={{
                        background: 'white',
                        left: '5px',
                        top: '36px',
                        bottom: '6px',
                        width: '1px',
                        position: 'absolute',
                      }}
                    />
                  </Box>
                )}
              </Timeline>
            </Flex>
            <CardRow>
              {e2.map(event => (
                <Box
                  key={`${event.id}${event.title}`}
                  css={{
                    maxWidth: 'calc(50% - 6px)',
                    width: '100%',
                    position: 'absolute',
                    top: `${calculateTopOffset(event.start, startTime)}px`,
                  }}
                >
                  <EventCard
                    {...event}
                    rescheduled={event.timeHasChanged}
                    small={isLessThan45Min(event)}
                    height={calculateEventCardHeight(event.start, event.end)}
                    title={event.act.title || event.title}
                    start={event.startTime}
                    end={event.endTime}
                    image={event.act.image.fluid}
                    venue={event.eventVenue.title}
                  />
                </Box>
              ))}
            </CardRow>
          </Flex>
        </Flex>
      </Box>
      <ColorOverlay />
    </Layout>
  )
}

SchemaPage.propTypes = {
  data: PropTypes.object.isRequired,
}

export const query = graphql`
  {
    flags: allContentfulFeatures {
      edges {
        node {
          hideSchema
        }
      }
    }
    events: allContentfulSchemaCollection {
      edges {
        node {
          artistEvents {
            id
            start(formatString: "YYYY-MM-DDTHH:mm+02:00")
            end(formatString: "YYYY-MM-DDTHH:mm+02:00")
            startTime: start(formatString: "HH:mm")
            endTime: end(formatString: "HH:mm")
            title
            cancelled
            timeHasChanged
            eventVenue {
              title
            }
            act {
              id
              slug
              image {
                fluid(resizingBehavior: FILL, maxHeight: 160, maxWidth: 160, cropFocus: FACES) {
                  base64
                  aspectRatio
                  src
                  srcSet
                  sizes
                }
              }
              name
            }
          }
        }
      }
    }
  }
`
export default SchemaPage
